/**
 * Logo background helpers, run in the browser. Logos usually sit on one flat
 * colour, so we read it from the image border and key it out, rather than
 * running a photo cutout model that struggles with text and thin shapes.
 */

const SAMPLE_EDGE = 256;
const MAX_EDGE = 2048;
/** Below this RGB distance a pixel is background; above OUTER it is logo. */
const INNER = 28;
const OUTER = 80;

async function loadBitmap(url: string): Promise<ImageBitmap> {
  const response = await fetch(url);
  if (!response.ok) throw new Error('Could not load the logo.');
  return createImageBitmap(await response.blob());
}

function draw(bitmap: ImageBitmap, maxEdge: number) {
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Your browser cannot edit images.');
  ctx.drawImage(bitmap, 0, 0, width, height);
  return { canvas, ctx, width, height };
}

function hex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;
}

function parseHex(value: string): [number, number, number] {
  return [1, 3, 5].map((i) => parseInt(value.slice(i, i + 2), 16)) as [number, number, number];
}

/**
 * The logo's background colour, read from its border. Null when the border is
 * mostly transparent or has no dominant colour.
 */
export async function sampleLogoBackground(url: string): Promise<string | null> {
  const bitmap = await loadBitmap(url);
  const { ctx, width, height } = draw(bitmap, SAMPLE_EDGE);
  bitmap.close();
  const data = ctx.getImageData(0, 0, width, height).data;
  const buckets = new Map<number, { count: number; r: number; g: number; b: number }>();
  let total = 0;
  let opaque = 0;
  const visit = (x: number, y: number) => {
    const i = (y * width + x) * 4;
    total += 1;
    if (data[i + 3] < 200) return;
    opaque += 1;
    const key = ((data[i] >> 4) << 8) | ((data[i + 1] >> 4) << 4) | (data[i + 2] >> 4);
    const bucket = buckets.get(key) ?? { count: 0, r: 0, g: 0, b: 0 };
    bucket.count += 1;
    bucket.r += data[i];
    bucket.g += data[i + 1];
    bucket.b += data[i + 2];
    buckets.set(key, bucket);
  };
  for (let x = 0; x < width; x += 1) {
    visit(x, 0);
    visit(x, height - 1);
  }
  for (let y = 1; y < height - 1; y += 1) {
    visit(0, y);
    visit(width - 1, y);
  }
  if (total === 0 || opaque / total < 0.6) return null;
  let best: { count: number; r: number; g: number; b: number } | null = null;
  for (const bucket of buckets.values()) if (!best || bucket.count > best.count) best = bucket;
  if (!best || best.count / opaque < 0.5) return null;
  return hex(best.r / best.count, best.g / best.count, best.b / best.count);
}

/**
 * A transparent PNG of the logo with its border colour keyed out, or null when
 * there is no solid background to remove.
 */
export async function removeLogoBackground(url: string): Promise<Blob | null> {
  const background = await sampleLogoBackground(url);
  if (!background) return null;
  const [br, bg, bb] = parseHex(background);
  const bitmap = await loadBitmap(url);
  const { canvas, ctx, width, height } = draw(bitmap, MAX_EDGE);
  bitmap.close();
  const image = ctx.getImageData(0, 0, width, height);
  const data = image.data;
  for (let i = 0; i < data.length; i += 4) {
    const dr = data[i] - br;
    const dg = data[i + 1] - bg;
    const db = data[i + 2] - bb;
    const distance = Math.sqrt(dr * dr + dg * dg + db * db);
    if (distance >= OUTER) continue;
    const keep = Math.max(0, (distance - INNER) / (OUTER - INNER));
    data[i + 3] = Math.round(data[i + 3] * keep);
    if (keep > 0) {
      // Strip the background tint from anti-aliased edges so they don't halo.
      data[i] = Math.min(255, Math.max(0, br + dr / keep));
      data[i + 1] = Math.min(255, Math.max(0, bg + dg / keep));
      data[i + 2] = Math.min(255, Math.max(0, bb + db / keep));
    }
  }
  ctx.putImageData(image, 0, 0);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not save the logo.'))), 'image/png');
  });
}
